import React from "react";
import { motion } from "framer-motion";
import {
  Wifi,
  Coffee,
  Bed,
  Key,
  Utensils,
  Tv,
  AirVent,
  Shield,
} from "lucide-react";
import "./Amenities.css";

const Amenities: React.FC = () => {
  const amenities = [
    {
      category: "Connectivity",
      items: [
        {
          icon: <Wifi size={24} />,
          name: "Free High-Speed WiFi",
          description: "Complimentary internet access throughout the property",
        },
        {
          icon: <Tv size={24} />,
          name: "Direct TV",
          description: "Direct TV in every room",
        },
      ],
    },
    {
      category: "Dining",
      items: [
        {
          icon: <Coffee size={24} />,
          name: "Nearby Coffee Shop",
          description: (
            <>
              Coffee, tea, and morning treats located right next door. Including{" "}
              <a
                href="https://seviervalleycoffee.com"
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary-400 hover:text-primary-300 font-medium transition-colors"
              >
                Sevier Valley Coffee
              </a>{" "}
              and{" "}
              <a
                href="https://share.google/7M8rHsfNLtRBSOMpQ"
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary-400 hover:text-primary-300 font-medium transition-colors"
              >
                The Soda Barn
              </a>
              .
            </>
          ),
        },
        {
          icon: <Utensils size={24} />,
          name: "Nearby Restaurants",
          description:
            "Local Resturants nearby including The World Famous Mom's Cafe and the local favorite The Hot Spot",
        },
      ],
    },
    {
      category: "Comfort",
      items: [
        {
          icon: <AirVent size={24} />,
          name: "Climate Control",
          description:
            "AC units and baseboard heating with cozy heat from no forced air ",
        },
        {
          icon: <Bed size={24} />,
          name: "Premium Bedding",
          description: "Plush mattresses and luxury linens",
        },
      ],
    },
    {
      category: "Safety",
      items: [
        {
          icon: <Shield size={24} />,
          name: "24/7 Security",
          description: "Round-the-clock security monitoring",
        },
        {
          icon: <Key size={24} />,
          name: "Secure Keycard Entry",
          description: "Keycard access for your room. ",
        },
      ],
    },
  ];

  const specialFeatures = [
    {
      title: "Historic Main Street",
      description:
        "Step back in time at local pioneer museum and view the old buildings on Main Street.",
      image:
        "https://s3-media0.fl.yelpcdn.com/bphoto/eN7otlUUuUS5oJjX_pzYNw/l.jpg",
    },
    {
      title: "WWII Site",
      description: "Check out the historic World War II CCC & POW Camp.",
      image:
        "https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcQQjsw1XOYPbxC6Q6WG8l_9AAiNjL6bEa8nHIy6JsRodSz7JdiVm_KAnJMX&s=10",
    },
    {
      title: "Blackhawk Arena Events",
      description:
        "Catch live rodeos, livestock shows, and local events at Salina’s premier indoor event hub.",
      image:
        "https://richfieldutah.com/wp-content/uploads/2024/11/461266926_8206537609458495_5194044379141579213_n.jpg",
    },
  ];

  return (
    <div className="amenities">
      {/* Hero Section */}
      <section className="amenities-hero">
        <div className="amenities-hero-background">
          <div className="amenities-hero-overlay"></div>
        </div>
        <div className="container">
          <motion.div
            className="amenities-hero-content"
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1>Our Amenities</h1>
            <p>
              Discover the perfect blend of luxury, comfort, and cleanliness
              that makes your stay at High Desert Inn truly exceptional.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Amenities Grid */}
      <section className="section amenities-grid-section">
        <div className="container">
          <motion.div
            className="section-title"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h2>Everything You Need</h2>
            <p>
              From essential comforts to luxury extras, we've thought of
              everything to make your stay perfect.
            </p>
          </motion.div>

          <div className="amenities-categories">
            {amenities.map((category, categoryIndex) => (
              <motion.div
                key={categoryIndex}
                className="amenity-category"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: categoryIndex * 0.1 }}
              >
                <h3>{category.category}</h3>
                <div className="amenity-items">
                  {category.items.map((item, itemIndex) => (
                    <motion.div
                      key={itemIndex}
                      className="amenity-item"
                      initial={{ opacity: 0, x: -20 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      transition={{
                        duration: 0.6,
                        delay: categoryIndex * 0.1 + itemIndex * 0.1,
                      }}
                      whileHover={{ y: -5 }}
                    >
                      <div className="amenity-icon">{item.icon}</div>
                      <div className="amenity-content">
                        <h4>{item.name}</h4>
                        <p>{item.description}</p>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Special Features */}
      <section className="section special-features-section">
        <div className="container">
          <motion.div
            className="section-title"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h2>Unique Experiences</h2>
            <p>
              Discover the unique experiences that you can enjoy while staying
              at The High Desert Inn.
            </p>
          </motion.div>

          <div className="special-features-grid">
            {specialFeatures.map((feature, index) => (
              <motion.div
                key={index}
                className="special-feature-card"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.2 }}
                whileHover={{ y: -10 }}
              >
                <div className="feature-image">
                  <img src={feature.image} alt={feature.title} />
                </div>
                <div className="feature-content">
                  <h3>{feature.title}</h3>
                  <p>{feature.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Amenities;
